import { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { api, qs } from '../../lib/api.js'
import {
  Badge, Card, Empty, ErrorBox, Field, Modal, Page, Select, Spinner, SubmitButton, useConfirm, useToast
} from '../../components/Ui.jsx'
import { FLAG, PL_STAGES, addDays, fmtDate, inr, lakh, todayISO } from '../../lib/format.js'

export default function Match () {
  const [params] = useSearchParams()
  const navigate = useNavigate()
  const toast = useToast()

  const rematchFrom = params.get('rematchFrom')
  const [farmers, setFarmers] = useState([])
  const [query, setQuery] = useState({
    farmerId: '',
    targetStockingDate: addDays(todayISO(), 8),
    quantityLakh: 10,
    plStage: '',
    salinityPpt: '',
    excludeHatcheryId: ''
  })
  const [result, setResult] = useState(null)
  const [error, setError] = useState(null)
  const [searching, setSearching] = useState(false)
  const [booking, setBooking] = useState(null)
  const [source, setSource] = useState(null)

  useEffect(() => {
    api.get('/operator/farmers').then(d => setFarmers(d.farmers)).catch(() => {})
  }, [])

  // Coming from a failed sample: pre-load the farmer, the same stocking date and
  // the quantity, and keep the hatchery that failed out of the results.
  useEffect(() => {
    if (!rematchFrom) return
    api.get(`/operator/orders/${rematchFrom}`).then(({ order }) => {
      setSource(order)
      setQuery(q => ({
        ...q,
        farmerId: order.farmer._id,
        targetStockingDate: order.targetStockingDate,
        quantityLakh: order.quantityLakh,
        plStage: order.plStage || '',
        salinityPpt: order.farmer.pondSalinityPpt ?? '',
        excludeHatcheryId: order.hatchery._id
      }))
    }).catch(err => toast.error(err.message))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rematchFrom])

  const farmer = useMemo(
    () => farmers.find(f => f._id === query.farmerId) || null,
    [farmers, query.farmerId]
  )

  const search = async e => {
    e?.preventDefault()
    setSearching(true)
    setError(null)
    try {
      setResult(await api.get(`/operator/match${qs(query)}`))
    } catch (err) {
      setError(err)
      setResult(null)
    } finally {
      setSearching(false)
    }
  }

  const set = (k, v) => setQuery(q => ({ ...q, [k]: v }))

  return (
    <Page
      title="Match & book"
      subtitle="The farmer is on the phone. Enter the stocking date and quantity; the desk shows every batch that can serve it inside the date window, ranked by flag, date fit and haul distance."
      crumb={rematchFrom ? <Link to={`/operator/orders/${rematchFrom}`}>← Back to the failed order</Link> : null}
    >
      {source && (
        <div className="alert warn">
          <div>
            <b>Urgent re-match for {source.farmer.name}</b>
            {source.orderCode} failed its sample at {source.sample?.survivalPct}% on {source.hatchery.name}.
            Same stocking date ({fmtDate(source.targetStockingDate)}), {lakh(source.quantityLakh)} needed, and that
            hatchery is excluded from the results below.
          </div>
        </div>
      )}

      <Card title="Farmer request">
        <form onSubmit={search}>
          <div className="formgrid">
            <Field label="Farmer" required hint={farmer ? `${farmer.place} · pond ${farmer.pondSalinityPpt ?? '—'} ppt` : 'Not on the list? Add them first.'}>
              <Select
                options={farmers.map(f => ({ value: f._id, label: `${f.name} — ${f.village || f.district || ''} (${f.phone})` }))}
                value={query.farmerId}
                onChange={v => {
                  set('farmerId', v)
                  const f = farmers.find(x => x._id === v)
                  if (f?.pondSalinityPpt != null) set('salinityPpt', f.pondSalinityPpt)
                }}
                placeholder="Choose the farmer…"
                required
              />
            </Field>
            <Field label="Target stocking date" required hint="Farmers can postpone, never stock early — the window runs forward only.">
              <input type="date" value={query.targetStockingDate} onChange={e => set('targetStockingDate', e.target.value)} required />
            </Field>
            <Field label="Quantity (lakh PL)" required>
              <input type="number" step="0.5" min="0" value={query.quantityLakh} onChange={e => set('quantityLakh', e.target.value)} required />
            </Field>
            <Field label="Preferred PL stage">
              <Select options={PL_STAGES} value={query.plStage} onChange={v => set('plStage', v)} placeholder="Any stage" />
            </Field>
            <Field label="Pond salinity (ppt)" hint="Used to show how far the batch has to be acclimatised.">
              <input type="number" step="0.5" value={query.salinityPpt} onChange={e => set('salinityPpt', e.target.value)} />
            </Field>
          </div>
          <div className="btnrow">
            <SubmitButton busy={searching} type="submit">Find available batches</SubmitButton>
            <Link className="btn" to="/operator/farmers">Add a new farmer</Link>
          </div>
        </form>
      </Card>

      {error && <ErrorBox error={error} onRetry={search} />}

      {result && (
        <Card
          title={`Batches ready ${fmtDate(result.window.from)} → ${fmtDate(result.window.to)}`}
          actions={<span className="muted small">{result.matches.length} batch(es) · window +{result.window.days} days</span>}
        >
          {!result.matches.length && (
            <Empty>
              No batch is ready in that window. Try a stocking date a few days later, or split the
              quantity across two hatcheries.
            </Empty>
          )}

          {result.matches.map(m => (
            <div key={m._id} className={`match-card ${m.bookable ? '' : 'blocked'}`}>
              <div className="top">
                <div>
                  <div className="name">{m.hatchery.name}</div>
                  <div className="where">
                    {[m.hatchery.village, m.hatchery.mandal].filter(Boolean).join(', ')} · <span className="mono">{m.batchCode}</span>
                  </div>
                  <div style={{ marginTop: 6, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    <Badge tone={FLAG[m.qualityFlag].tone}>{FLAG[m.qualityFlag].label}</Badge>
                    {m.stageMatch === true && <Badge tone="good">Stage match</Badge>}
                    {m.stageMatch === false && <Badge tone="warn">{m.plStage} (asked {query.plStage})</Badge>}
                    {m.pcr?.reportPath && <Badge tone="info">PCR on file</Badge>}
                    {m.daysAfterTarget === 0
                      ? <Badge tone="good">Ready on the date</Badge>
                      : <Badge tone="warn">+{m.daysAfterTarget} day{m.daysAfterTarget > 1 ? 's' : ''}</Badge>}
                  </div>
                  {m.pricing.offerLabel && <div className="pill-offer" style={{ marginTop: 6 }}>{m.pricing.offerLabel}</div>}
                  {m.promoNote && <div className="muted small" style={{ marginTop: 6 }}>{m.promoNote}</div>}
                </div>
                <div className="price">
                  <div className="big">{inr(m.pricing.total)}</div>
                  <div className="muted small">
                    {inr(m.basePricePer1000)} / 1,000 PL
                    {m.pricing.discount > 0 && <><br />less {inr(m.pricing.discount)} offer</>}
                    {m.pricing.bonusLakh > 0 && <><br />+{lakh(m.pricing.bonusLakh)} free</>}
                  </div>
                  <div className="btnrow" style={{ justifyContent: 'flex-end', marginTop: 8 }}>
                    {m.pcr?.reportPath && <a className="btn sm" href={m.pcr.reportPath} target="_blank" rel="noreferrer">PCR</a>}
                    {m.media?.length > 0 && (
                      <a className="btn sm" href={m.media[0].path} target="_blank" rel="noreferrer">Photos ({m.media.length})</a>
                    )}
                    {m.bookable
                      ? <button className="btn primary sm" onClick={() => setBooking(m)}>Block & book</button>
                      : m.qualityFlag === 'red_flag'
                        ? <Link className="btn danger sm" to={`/operator/batches/${m._id}/flag`}>Red flag — decide</Link>
                        : <button className="btn sm" disabled>Cannot book</button>}
                  </div>
                </div>
              </div>

              <div className="facts">
                <div><div className="k">Ready</div><div className="v">{fmtDate(m.readyDate)}</div></div>
                <div><div className="k">Stage</div><div className="v">{m.plStage}</div></div>
                <div><div className="k">Available</div><div className="v">{lakh(m.availableLakh)}</div></div>
                <div><div className="k">Distance</div><div className="v">{m.distanceKm != null ? `${m.distanceKm} km` : '—'}</div></div>
                <div><div className="k">Est. haul</div><div className="v">{m.estHours != null ? `${m.estHours} hrs` : '—'}</div></div>
                <div>
                  <div className="k">Salinity</div>
                  <div className="v">{m.salinityPpt ?? '—'} ppt{m.salinityGap != null && <span className="muted small"> (Δ{m.salinityGap})</span>}</div>
                </div>
                <div><div className="k">Stress test</div><div className="v">{m.stressTestPct ?? '—'}%</div></div>
                <div><div className="k">Broodstock</div><div className="v" style={{ fontSize: 12 }}>{m.broodstockSource}</div></div>
              </div>

              {!m.bookable && (
                <div className="hint" style={{ color: 'var(--bad)' }}>Blocked: {m.blockers.join(' · ')}</div>
              )}
            </div>
          ))}
        </Card>
      )}

      {booking && (
        <BookModal
          match={booking}
          farmer={farmer}
          query={query}
          source={source}
          settings={result.settings}
          onClose={() => setBooking(null)}
          onDone={order => {
            toast.ok(`${order.orderCode} created. Collect the advance to block the capacity.`)
            navigate(`/operator/orders/${order._id}`)
          }}
        />
      )}
    </Page>
  )
}

function BookModal ({ match, farmer, query, source, settings, onClose, onDone }) {
  const toast = useToast()
  const confirm = useConfirm()
  const qty = Number(query.quantityLakh)
  const suggested = Math.max(5000, Math.round((match.pricing.total * settings.advance_pct) / 100 / 5000) * 5000)

  const [advance, setAdvance] = useState(suggested)
  const [notes, setNotes] = useState(source ? `Urgent re-match after failed sample on ${source.batch?.batchCode || source.orderCode}` : '')
  const [busy, setBusy] = useState(false)

  const submit = async e => {
    e.preventDefault()
    const ok = await confirm({
      title: 'Place this booking?',
      body: `${lakh(qty)} is held informally at ${match.hatchery.name} and the farmer is told to send the advance. Capacity is only blocked once that advance is verified.`,
      facts: [
        ['Farmer', farmer?.name || 'selected farmer'],
        ['Batch', `${match.batchCode} · ${match.plStage}`],
        ['Quantity', lakh(qty)],
        ['Stocking date', fmtDate(query.targetStockingDate)],
        ['Order value', inr(match.pricing.total)],
        ['Advance to collect', inr(advance)]
      ],
      confirmLabel: 'Place booking'
    })
    if (!ok) return
    setBusy(true)
    try {
      const { order } = await api.post('/operator/orders', {
        farmerId: query.farmerId,
        batchId: match._id,
        quantityLakh: qty,
        targetStockingDate: query.targetStockingDate,
        advanceAmount: advance,
        notes,
        isUrgent: Boolean(source),
        replacesOrderId: source?._id
      })
      onDone(order)
    } catch (err) {
      toast.error(err.message)
      setBusy(false)
    }
  }

  const sampleDue = addDays(query.targetStockingDate, -settings.sample_lead_days)

  return (
    <Modal title={`Book ${lakh(qty)} from ${match.hatchery.name}`} onClose={onClose}>
      <form onSubmit={submit}>
        <div className="kv" style={{ marginBottom: 14 }}>
          <div><div className="k">Farmer</div><div className="v">{farmer?.name}</div></div>
          <div><div className="k">Batch</div><div className="v mono">{match.batchCode}</div></div>
          <div><div className="k">Stocking date</div><div className="v">{fmtDate(query.targetStockingDate)}</div></div>
          <div><div className="k">Ready date</div><div className="v">{fmtDate(match.readyDate)}</div></div>
          <div><div className="k">Order value</div><div className="v big">{inr(match.pricing.total)}</div></div>
          <div><div className="k">Haul</div><div className="v">{match.distanceKm} km · {match.estHours} hrs</div></div>
        </div>

        <div className="alert info">
          <div>
            <b>A mandatory sample balloon goes out first.</b>
            {settings.sample_animals} animals must reach {farmer?.name || 'the farmer'} by <b>{fmtDate(sampleDue)}</b>
            {' '}for the 24-hour pond survival test. Dispatch only happens if survival is {settings.pass_threshold_pct}% or better.
          </div>
        </div>

        <Field label="Advance to block the batch (₹)" required hint={`Suggested ${settings.advance_pct}% of the order value, rounded. Capacity is blocked only once this is verified.`}>
          <input type="number" step="1" min="1" max={match.pricing.total} value={advance} onChange={e => setAdvance(e.target.value)} required />
        </Field>
        <Field label="Notes for the file">
          <textarea value={notes} onChange={e => setNotes(e.target.value)} placeholder="Anything the desk should remember about this booking" />
        </Field>

        <div className="btnrow">
          <SubmitButton busy={busy} type="submit">Create booking</SubmitButton>
          <button type="button" className="btn" onClick={onClose}>Cancel</button>
        </div>
      </form>
    </Modal>
  )
}
